import { FC, useState } from 'react'
import { Box, Collapse, Typography } from '@mui/material'
import { AssetAttribute, AssetColumn, AssetColumnKind, AssetRow } from 'models'
import ChevronRight from 'ui/components/svg/ChevronRight'
import OnyxiaTooltip from 'ui/components/tooltip/OnyxiaTooltip'
import { SectionWrapper } from './assetDetailsPane.styles'
import AttributeSources from './AttributeSources'
import { DATE_TIME_DISPLAY, formatCellValue } from '../utils/assetRows'

interface Props {
  column: AssetColumn
  row: AssetRow
  attribute?: AssetAttribute
}

/**
 * One resolved value per attribute — the majority, or the earliest/most recent for First/Last
 * dates. `+N` opens the values the other members reported, each beside the instances that
 * reported it. Until the details load the row's own count stands in, and does not open.
 *
 * An array's resolved value is the union of every member's array, which matches no single entry,
 * so arrays head with `values[0]` — the server orders values most-supported first.
 */
const AttributeSection: FC<Props> = ({ column, row, attribute }) => {
  const [expanded, setExpanded] = useState(false)

  const values = attribute?.values ?? []
  const isArray = column.kind === AssetColumnKind.Array
  const resolvedIndex = isArray
    ? 0
    : Math.max(
        0,
        values.findIndex((value) => value.text === attribute?.resolved)
      )
  const resolved = attribute ? (isArray ? values[0]?.text ?? null : attribute.resolved) : row.values[column.name]
  const others = values.filter((_, index) => index !== resolvedIndex)
  const more = attribute ? others.length : row.badges[column.name] ?? 0

  return (
    <SectionWrapper>
      <Box className="section-header">
        <Typography className="section-label">{column.displayName}</Typography>

        {!!more &&
          (others.length ? (
            <Box
              component="button"
              type="button"
              className="section-badge is-toggle"
              aria-expanded={expanded}
              aria-label={`Show ${more} other ${more === 1 ? 'value' : 'values'} for ${column.displayName}`}
              onClick={() => setExpanded(!expanded)}
            >
              +{more}
              <Box component="span" className={`badge-chevron ${expanded ? 'is-open' : ''}`}>
                <ChevronRight />
              </Box>
            </Box>
          ) : (
            <OnyxiaTooltip title={`${more} other ${more === 1 ? 'value' : 'values'} reported`} placement="top">
              <Box component="span" className="section-badge">
                +{more}
              </Box>
            </OnyxiaTooltip>
          ))}
      </Box>

      <Box className="value-row">
        <Typography className="value-text">{formatCellValue(resolved, column.kind, DATE_TIME_DISPLAY)}</Typography>
        {!!values[resolvedIndex] && <AttributeSources sources={values[resolvedIndex].sources} />}
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box className="value-list">
          {others.map((value) => (
            <Box key={value.text} className="value-row is-other">
              <Typography className="value-text">
                {formatCellValue(value.text, column.kind, DATE_TIME_DISPLAY)}
              </Typography>
              <AttributeSources sources={value.sources} />
            </Box>
          ))}
        </Box>
      </Collapse>
    </SectionWrapper>
  )
}

export default AttributeSection
